import { ErrorMessage } from 'views/components';
import PropTypes from 'prop-types';
import moment from 'moment';
import { turnsActions } from 'core/Turns';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Container,
    DateSelect,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    Modal,
    Text
} from 'mol-ui';
import { Col, Row } from 'reactstrap';
import {
    DoctorPicker,
    PatientPicker,
    SpecialtyPicker
} from 'views/pickers';
import React, { useEffect } from 'react';
import {
    dateToObject,
    objectToDate,
    validationSchema
} from 'shared/utils';
import {
    useCopyToClipboard,
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';

const DEFAULT_FORM = {
    doctor_id: null,
    from_date: dateToObject(moment()),
    from_time: moment().format('HH:mm'),
    patient_id: null,
    specialty_id: null
};

const ExternalTurnModal = ({
    active,
    onToggle
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        externalAppointment: {
            doctor_url: doctorUrl,
            url: patientUrl
        },
        externalAppointment,
        externalAppointmentLoading: loading
    } = useSelector('turns');

    const [professionalUrlcopy, professionalUrlcopied] = useCopyToClipboard();
    const [patientUrlCopy, patientUrlCopied] = useCopyToClipboard();
    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const [form, setForm, setFormValue] = useObjectState({ ...DEFAULT_FORM });

    useEffect(() => {
        if (active) {
            setForm({
                ...DEFAULT_FORM,
                from_date: dateToObject(moment()),
                from_time: moment().format('HH:mm')
            });
        }
    }, [active, setForm]);

    const handleChange = id => value => {
        id === 'doctor_id' && clearError('specialty_id');
        clearError(id);
        setFormValue(id, value);
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const body = {
            ...(!!form.doctor_id  ? { doctor_id: form.doctor_id } : { specialty_id: form.specialty_id }),
            from_date_time: `${ objectToDate(form.from_date).format('YYYY-MM-DD') } ${ form.from_time }`
        };
        dispatch(turnsActions.createExternalAppointment({ body, patientId: form.patient_id }));
    };
    
    const onCancel = () => {
        clearErrors();
        dispatch(turnsActions.setTurnsExternalAppointment({}));
        onToggle();
    };
    
    const validate = () => {
        const { errors, isValid } = validationSchema({
            patient_id: [!!form.patient_id, t('Requerido')],
            specialty_id: [!!form.specialty_id || !!form.doctor_id, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };
    
    return (
        <Modal
            active={ active }
            padding="20px 10px"
            width="900px"
            onToggle={ onToggle }
        >
            <Text
                fontSize="20px"
                margin="0px 0px 30px 0px"
                textAlign="center"
            >
                { t('Generando turno externo') }
            </Text>
            <Row>
                <Col sm="6" xs="12">
                    <FormGroup label={ t('Profesional') } margin="0px 0px 10px 0px">
                        <DoctorPicker
                            hasError={ !!errors?.specialty_id }
                            specialty={ form?.specialty_id }
                            value={ form?.doctor_id }
                            width="120"
                            onChange={ handleChange('doctor_id') }
                        />
                    </FormGroup>
                </Col>
                <Col sm="6" xs="12">
                    <FormGroup label={ t('Paciente') } margin="0px 0px 10px 0px">
                        <PatientPicker
                            allowNone={ false }
                            hasError={ !!errors?.patient_id }
                            value={ form?.patient_id }
                            width="120"
                            onChange={ handleChange('patient_id') }
                        />
                        <ErrorMessage error={ errors.patient_id } marginLeft={ 0 } marginTop={ 10 } relative={ true } />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col sm="6" xs="12">
                    <FormGroup label={ t('Especialidad') } margin="0px 0px 10px 0px">
                        <SpecialtyPicker
                            fontSize="12px"
                            hasError={ !!errors?.specialty_id }
                            value={ form?.specialty_id }
                            width="120"
                            onChange={ handleChange('specialty_id') }
                        />
                        <ErrorMessage error={ errors.specialty_id } marginLeft={ 0 } marginTop={ 10 } relative={ true } />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col sm="8" xs="12">
                    <FormGroup label={ t('Fecha') } margin="0px 0px 10px 0px">
                        <DateSelect
                            nextYears={ 1 }
                            value={ form.from_date }
                            onChange={ handleChange('from_date') }
                        />
                    </FormGroup>
                </Col>
                <Col sm="4" xs="12">
                    <FormGroup label={ t('Horario') } margin="0px 0px 10px 0px">
                        <Input
                            type="time"
                            value={ form.from_time }
                            width="100%"
                            onChange={ handleChange('from_time') }
                        />
                    </FormGroup>
                </Col>
            </Row>
            {
                !!externalAppointment &&
                <>
                    {
                        !!patientUrl &&
                        <Row>
                            <FormGroup label={ t('URL del paciente') } margin="10px 0px 20px 0px">
                                <Container alignItems="center" flexDirection="row">
                                    <Text fontSize="14px" margin="0px 5px 0px 0px">
                                        { patientUrl }
                                    </Text>
                                    <IconButton
                                        icon={ patientUrlCopied ? 'done' : 'content_copy' }
                                        size={ 14 }
                                        onClick={ patientUrlCopied ? () => null : () => patientUrlCopy(patientUrl) }
                                    />
                                </Container>
                            </FormGroup>
                        </Row>
                    }
                    {
                        !!doctorUrl &&
                        <Row>
                            <FormGroup label={ t('URL del profesional') } margin="0px 0px 30px 0px">
                                <Container alignItems="center" flexDirection="row">
                                    <Text fontSize="14px" margin="0px 5px 0px 0px">
                                        { doctorUrl }
                                    </Text>
                                    <IconButton
                                        icon={ professionalUrlcopied ? 'done' : 'content_copy' }
                                        size={ 14 }
                                        onClick={ professionalUrlcopied ? () => null : () => professionalUrlcopy(doctorUrl) }
                                    />
                                </Container>
                            </FormGroup>
                        </Row>
                    }
                </>
            }
            <ButtonFooter margin="30px 0px 0px 0px">
                <ExpandButton
                    danger
                    caption={ t('Cancelar') }
                    size={ 120 }
                    onClick={ onCancel }
                />
                <ExpandButton
                    caption={ t('Aceptar') }
                    loading={ loading }
                    size={ 120 }
                    onClick={ handleSubmit }
                />
            </ButtonFooter>
        </Modal>
    );
};

ExternalTurnModal.propTypes = {
    active: PropTypes.bool,
    onToggle: PropTypes.func
};

export default ExternalTurnModal;
